import React, { useMemo, useState } from 'react';
import { Loader2, X, Search } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import client from '../../api/client';
import MealSelect from './MealSelect';

const SOURCES = [['in_person', 'In person'], ['called', 'Phone call'], ['texted', 'Text'], ['emailed', 'Email']];
const pill = (on, tone) => `px-3 py-1 rounded-full text-xs border transition-colors ${on ? tone : 'border-[#EFE3D8] text-[#6A6A6A] hover:border-[#B8896C]'}`;

const Row = ({ g, d, set, mealOptions }) => (
  <tr data-testid={`quick-rsvp-row-${g.id}`} className="border-t border-[#F2E8DE]">
    <td className="py-2 pr-3">
      <p className="text-sm text-[#2A2A2A]">{g.name}</p>
      <p className="text-[11px] text-[#9A8A7E]">currently {g.rsvp}{g.plusOne ? ' · has +1' : ''}</p>
    </td>
    <td className="py-2 pr-3 whitespace-nowrap space-x-1">
      <button data-testid={`quick-yes-${g.id}`} onClick={() => set({ rsvp: d.rsvp === 'Yes' ? undefined : 'Yes' })} className={pill(d.rsvp === 'Yes', 'bg-[#EAF2E5] border-[#9BC28A] text-[#2F5D2F]')}>Yes</button>
      <button data-testid={`quick-no-${g.id}`} onClick={() => set({ rsvp: d.rsvp === 'No' ? undefined : 'No' })} className={pill(d.rsvp === 'No', 'bg-[#FBEAEA] border-[#E3A5A5] text-[#8A2B2B]')}>No</button>
    </td>
    <td className="py-2 pr-3">
      {g.plusOne && d.rsvp === 'Yes' ? (
        <label className="text-xs inline-flex items-center gap-1.5 cursor-pointer">
          <input data-testid={`quick-plusone-${g.id}`} type="checkbox" className="accent-[#7B2C3B]" checked={!!d.plusOneComing} onChange={(e) => set({ plusOneComing: e.target.checked })} /> +1 coming
        </label>
      ) : <span className="text-[11px] text-[#C9B8AA]">—</span>}
    </td>
    <td className="py-2">
      {d.rsvp === 'Yes' ? (
        <div className="flex flex-col gap-1">
          <MealSelect options={mealOptions} testId={`quick-meal-${g.id}`} value={d.meal ?? g.meal} onChange={(v) => set({ meal: v })} placeholder="Meal" className="w-28" />
          {g.plusOne && (d.plusOneComing ?? g.plusOneComing) && (
            <MealSelect options={mealOptions} testId={`quick-plusone-meal-${g.id}`} value={d.plusOneMeal ?? g.plusOneMeal} onChange={(v) => set({ plusOneMeal: v })} placeholder="+1 meal" className="w-28" />
          )}
        </div>
      ) : <span className="text-[11px] text-[#C9B8AA]">—</span>}
    </td>
  </tr>
);

// Fast entry for RSVPs guests gave verbally — logged as manual RSVPs.
export const QuickRsvpEntry = ({ open, onOpenChange, guests, onSaved, toast, mealOptions }) => {
  const [drafts, setDrafts] = useState({});
  const [q, setQ] = useState('');
  const [pendingOnly, setPendingOnly] = useState(true);
  const [source, setSource] = useState('in_person');
  const [busy, setBusy] = useState(false);

  const list = useMemo(() => guests.filter((g) =>
    (!pendingOnly || g.rsvp === 'Pending' || drafts[g.id]) && (!q || g.name.toLowerCase().includes(q.toLowerCase()))), [guests, q, pendingOnly, drafts]);
  const changed = Object.entries(drafts).filter(([, d]) => d.rsvp);

  const save = async () => {
    setBusy(true);
    try {
      const items = changed.map(([guestId, d]) => ({ guestId, rsvp: d.rsvp, plusOneComing: d.plusOneComing ?? null, meal: d.meal ?? null, plusOneMeal: d.plusOneMeal ?? null }));
      const { data } = await client.post('/guests/bulk-manual-rsvp', { items, source });
      toast?.({ title: `${data.saved} RSVP${data.saved === 1 ? '' : 's'} saved`, description: data.errors.length ? `${data.errors.length} couldn't be saved.` : 'Your update texts say "(logged by you)".' });
      setDrafts({});
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast?.({ title: 'Could not save', description: e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="quick-rsvp-dialog" className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Quick RSVP entry</DialogTitle>
          <DialogDescription>Tap through answers guests told you in person, by phone or by text, then save them all at once.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#9A8A7E]" />
            <input data-testid="quick-rsvp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a guest" className="w-full pl-8 pr-3 py-2 rounded-full border border-[#EFE3D8] text-sm" />
          </div>
          <label className="text-xs inline-flex items-center gap-1.5"><input data-testid="quick-rsvp-pending-only" type="checkbox" className="accent-[#7B2C3B]" checked={pendingOnly} onChange={(e) => setPendingOnly(e.target.checked)} /> Pending only</label>
          <select data-testid="quick-rsvp-source" value={source} onChange={(e) => setSource(e.target.value)} className="text-xs px-3 py-2 rounded-full border border-[#EFE3D8] bg-white">
            {SOURCES.map(([v, l]) => <option key={v} value={v}>They told us: {l}</option>)}
          </select>
        </div>
        <div className="overflow-auto flex-1 min-h-0">
          <table className="w-full">
            <tbody>{list.map((g) => <Row key={g.id} g={g} mealOptions={mealOptions} d={drafts[g.id] || {}} set={(p) => setDrafts((all) => ({ ...all, [g.id]: { ...(all[g.id] || {}), ...p } }))} />)}</tbody>
          </table>
          {list.length === 0 && <p className="text-sm text-[#9A8A7E] py-6 text-center">No guests match.</p>}
        </div>
        <div className="flex items-center justify-between gap-3 pt-2 border-t border-[#F2E8DE]">
          <span data-testid="quick-rsvp-count" className="text-xs text-[#6A6A6A]">{changed.length} answer{changed.length === 1 ? '' : 's'} ready to save</span>
          <div className="flex gap-2">
            <button onClick={() => onOpenChange(false)} className="text-xs px-4 py-2 rounded-full border border-[#EFE3D8] inline-flex items-center gap-1"><X className="w-3 h-3" /> Close</button>
            <button data-testid="quick-rsvp-save" disabled={!changed.length || busy} onClick={save} className="text-xs px-4 py-2 rounded-full bg-[#7B2C3B] text-white disabled:opacity-40 inline-flex items-center gap-1.5">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save all
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuickRsvpEntry;
